import { useEffect, useReducer, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { AppointmentDialogFooter } from "../AppointmentDialogFooter"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { extractApiError } from "@/lib/api-errors"
import { useSetBookingStatusMutation } from "../../bookingCalendarApi"
import {
  BookingRequestStatus,
  OUTCOME_REFRESHING_MESSAGE,
  isCorrection,
  nextOutcomeRefresh,
  outcomeGate,
  outcomeOpensMessage,
  statusLabel,
  statusNeedsReason,
  type OutcomeGate,
} from "../../lib/bookingStatus"
import type { BookingAction, BookingActionContext, BookingActionFormProps } from "./types"
import { MAX_NOTE_LENGTH } from "./types"
import { useReportBusy } from "./useReportBusy"

/**
 * Records the appointment's outcome, or corrects it. Which statuses a given person may pick is the
 * server's call, sent with the booking's status (see `BookingStatusView.allowedStatuses`).
 *
 * Before the appointment starts the server offers nothing (an outcome is refused until then — F7),
 * so for someone who could record one the action is shown disabled, saying from when, rather than
 * silently missing. At the start the dialog fetches the status view again (`useOutcomeGateRefresh`),
 * and until the fresh list arrives the action stays drawn, disabled. See `outcomeGate`.
 */
export const StatusAction: BookingAction = {
  id: "updatingStatus",
  label: "Update status",
  triggerVariant: "outline",
  isAvailable: ctx => gateFor(ctx).state !== "hidden",
  disabledReason: ctx => reasonFor(gateFor(ctx), ctx.timeZone),
  Form: StatusForm,
}

/**
 * Whether the person looking could record an outcome here at all. An administrator can on any
 * booking. A host can on their own, but before the start the server's list is empty for host and
 * non-host alike, so only the server can say which they are: `canUpdate` on the status view.
 */
function mayUpdate(ctx: BookingActionContext): boolean {
  return ctx.canManage || ctx.statusView?.canUpdate === true
}

export function gateFor(ctx: BookingActionContext, now: Date = new Date()): OutcomeGate {
  const view = ctx.statusView
  if (!view) return { state: "hidden" }

  return outcomeGate({
    status: view.status,
    allowedStatuses: view.allowedStatuses ?? [],
    outcomeOpensAt: view.outcomeOpensAt,
    mayUpdate: mayUpdate(ctx),
    viewFetchedAt: ctx.statusFetchedAt,
  }, now)
}

function reasonFor(gate: OutcomeGate, timeZone: string): string | undefined {
  if (gate.state === "notYet") return outcomeOpensMessage(gate.opensAt, timeZone)
  if (gate.state === "refreshing") return OUTCOME_REFRESHING_MESSAGE
  return undefined
}

/** setTimeout's longest delay; a later moment just wakes early and schedules again. */
const MAX_TIMER_MS = 2 ** 31 - 1

/**
 * Keeps the status view in step with the clock for the outcome gate: fetches it again when the
 * appointment starts (the list fetched before is always empty), and again shortly after while the
 * gate says it is `refreshing`. The server's fresh list then decides whether the action opens.
 */
export function useOutcomeGateRefresh(ctx: BookingActionContext, refetch: () => unknown, fetching: boolean) {
  const [tick, wake] = useReducer((n: number) => n + 1, 0)
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  const at = fetching || !ctx.statusView ? undefined : nextOutcomeRefresh(gateFor(ctx), ctx.statusFetchedAt)

  useEffect(() => {
    if (at === undefined) return
    const delay = Math.min(Math.max(at - Date.now(), 0), MAX_TIMER_MS)
    const timer = setTimeout(() => {
      // Redraw with the new time (the gate reads the clock) — which also schedules again after a
      // capped wait that woke early — then, if the moment has come, ask the server.
      wake()
      if (Date.now() >= at) refetchRef.current()
    }, delay)
    return () => clearTimeout(timer)
  }, [at, tick])
}

function StatusForm({ ctx, onDone, onBusyChange }: BookingActionFormProps) {
  const [setBookingStatus, statusState] = useSetBookingStatusMutation()
  const [note, setNote] = useState("")
  const [targetStatus, setTargetStatus] = useState<string>("")
  const [error, setError] = useState<string | null>(null)

  const busy = statusState.isLoading
  useReportBusy(onBusyChange, busy)

  const { requestId, statusView } = ctx
  const allowedStatuses = statusView?.allowedStatuses ?? []
  const notYet = reasonFor(gateFor(ctx), ctx.timeZone)

  // Mirrors the server: Other and Rescheduled always need a reason, and so does changing an outcome
  // somebody already recorded. The server still decides; this only keeps the button honest.
  const chosenStatus = targetStatus ? Number(targetStatus) : undefined
  const statusReasonRequired = chosenStatus !== undefined && statusView !== undefined
    && (statusNeedsReason(chosenStatus) || isCorrection(statusView.status, chosenStatus))
  const statusSubmittable = chosenStatus !== undefined && (!statusReasonRequired || note.trim().length > 0)

  async function submitStatus() {
    if (!requestId || chosenStatus === undefined || !statusSubmittable) return

    setError(null)

    const result = await setBookingStatus({
      requestId,
      status: chosenStatus,
      reason: note.trim() || undefined,
    })

    if ("error" in result) {
      setError(describeStatusFailure(result.error))
      return
    }

    // Closes back to the booking: its status section refetches and shows what was recorded, which is
    // the confirmation. The worklist behind it drops the row on the same invalidation.
    onDone()
  }

  if (notYet) {
    return <NotYetForm reason={notYet} onDone={onDone} />
  }

  return (
    <>
      {statusView && (
        <section>
          <Label htmlFor="booking-status" className="text-xs font-medium">
            What happened
          </Label>
          <Select value={targetStatus} onValueChange={setTargetStatus} disabled={busy}>
            <SelectTrigger id="booking-status" className="mt-1.5 w-full">
              <SelectValue placeholder="Choose a status" />
            </SelectTrigger>
            <SelectContent>
              {allowedStatuses.map(status => (
                <SelectItem key={status} value={String(status)}>
                  {statusLabel(status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Label htmlFor="status-reason" className="mt-3 block text-xs font-medium">
            Reason{" "}
            <span className="text-muted-foreground">{statusReasonRequired ? "(required)" : "(optional)"}</span>
          </Label>
          <Textarea
            id="status-reason"
            className="mt-1.5"
            rows={2}
            maxLength={MAX_NOTE_LENGTH}
            value={note}
            disabled={busy}
            placeholder={chosenStatus === BookingRequestStatus.Other ? "What happened" : "e.g. Recorded against the wrong appointment"}
            onChange={event => setNote(event.target.value)}
          />
          {chosenStatus !== undefined && isCorrection(statusView.status, chosenStatus) && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              This changes an outcome already recorded as {statusLabel(statusView.status)}.
            </p>
          )}
        </section>
      )}

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <AppointmentDialogFooter>
        <Button
          variant="outline"
          disabled={busy}
          onClick={onDone}
        >
          Cancel
        </Button>
        <Button disabled={busy || !statusSubmittable} onClick={submitStatus}>
          {statusState.isLoading ? "Saving…" : "Save status"}
        </Button>
      </AppointmentDialogFooter>
    </>
  )
}

/**
 * What the armed action shows before the appointment starts, while the dialog shell still draws the
 * trigger enabled: the reason, and a Save that cannot be pressed, explained by the same tooltip.
 */
function NotYetForm({ reason, onDone }: { reason: string; onDone: () => void }) {
  return (
    <>
      <p className="text-sm text-muted-foreground">{reason}.</p>

      <AppointmentDialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <TooltipProvider>
          <Tooltip>
            {/* A disabled button fires no pointer events, so the tooltip hangs off a focusable wrapper. */}
            <TooltipTrigger asChild>
              <span tabIndex={0} aria-label={reason}>
                <Button disabled className="pointer-events-none">Save status</Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{reason}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </AppointmentDialogFooter>
    </>
  )
}

/** Turns a refused status change into something worth reading — the server says why. */
function describeStatusFailure(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }
  const detail = extractApiError(failure?.data)

  if (detail) return detail
  if (failure?.status === 403) return "You can only update the status of appointments you host."
  if (failure?.status === 404) return "This booking could not be found."

  return "The status could not be saved."
}
